import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ClipboardList, 
  Plus, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  ChevronRight, 
  ChevronLeft,
  Edit2,
  Trash2,
  Users,
  UserCheck,
  FileText,
  LayoutGrid,
  Eye,
  Calendar,
  Filter,
  CalendarDays,
  Target
} from 'lucide-react';
import { getPlainTextPreview, formatDateSafe, isDeadlineOverdue } from '../utils/textUtils';
import { UserAvatar, ClickableName, AvatarStack } from './UserAvatar';
import WorkPlanning from './WorkPlanning';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CNESSTasks = ({ user }) => {
  const navigate = useNavigate();
  const [myTasks, setMyTasks] = useState([]);
  const [commonTasks, setCommonTasks] = useState([]);
  const [teamTasks, setTeamTasks] = useState([]);
  const [assignedByMeTasks, setAssignedByMeTasks] = useState([]);
  const [managementTasks, setManagementTasks] = useState([]); // For Task Management view
  const [completedTasks, setCompletedTasks] = useState([]); // For Completed Tasks view
  const [users, setUsers] = useState([]); // For user name lookup
  const [mySOPs, setMySOPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assigned'); // 'assigned', 'assigned_by_me', 'common', 'team', 'management', or 'completed'
  
  // Date filter for completed tasks
  const [completedDateFrom, setCompletedDateFrom] = useState('');
  const [completedDateTo, setCompletedDateTo] = useState('');
  
  // Pagination for assigned tasks
  const [myTasksPage, setMyTasksPage] = useState(1);
  const [myTasksTotalPages, setMyTasksTotalPages] = useState(1);
  const [myTasksTotal, setMyTasksTotal] = useState(0);
  
  // Pagination for common tasks
  const [commonTasksPage, setCommonTasksPage] = useState(1);
  const [commonTasksTotalPages, setCommonTasksTotalPages] = useState(1);
  const [commonTasksTotal, setCommonTasksTotal] = useState(0);
  
  // Pagination for team tasks
  const [teamTasksPage, setTeamTasksPage] = useState(1);
  const [teamTasksTotalPages, setTeamTasksTotalPages] = useState(1);
  const [teamTasksTotal, setTeamTasksTotal] = useState(0);
  
  // Pagination for management (all tasks) view
  const [managementPage, setManagementPage] = useState(1);
  const [managementTotalPages, setManagementTotalPages] = useState(1);
  const [managementTotal, setManagementTotal] = useState(0);
  
  // Pagination for completed tasks
  const [completedPage, setCompletedPage] = useState(1);
  const [completedTotalPages, setCompletedTotalPages] = useState(1);
  const [completedTotal, setCompletedTotal] = useState(0);

  const PAGE_SIZE = 10;

  useEffect(() => {
    fetchMyTasks();
    fetchCommonTasks();
    fetchUsers();
    fetchCompletedTasks();
    if (user?.role === 'manager') {
      fetchTeamTasks();
    }
    if (user?.role === 'manager' || user?.role === 'ceo' || user?.role === 'team_lead' || user?.role === 'superadmin') {
      fetchAssignedByMeTasks();
      fetchManagementTasks();
    }
    fetchMySOPs();
  }, []);

  useEffect(() => {
    fetchMyTasks();
  }, [myTasksPage]);

  useEffect(() => {
    fetchCommonTasks();
  }, [commonTasksPage]);
  
  useEffect(() => {
    if (user?.role === 'manager') {
      fetchTeamTasks();
    }
  }, [teamTasksPage]);

  const fetchMySOPs = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/sops/assigned`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMySOPs(res.data || []);
    } catch (error) {
      console.error('Failed to fetch SOPs:', error);
    }
  };

  const fetchMyTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/tasks?dimension=cness_tasks&page=${myTasksPage}&page_size=${PAGE_SIZE}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter tasks assigned to current user (array-based, user ID is in assigned_to array)
      // Also filter by dimension - check if 'cness_tasks' is in the dimension array
      const assignedTasks = (res.data.tasks || []).filter(t => 
        t.assigned_to && Array.isArray(t.assigned_to) && t.assigned_to.includes(user?.id) &&
        t.dimension && Array.isArray(t.dimension) && t.dimension.includes('cness_tasks')
      );
      
      setMyTasks(assignedTasks);
      setMyTasksTotalPages(res.data.total_pages || 1);
      setMyTasksTotal(assignedTasks.length);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommonTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/tasks?dimension=cness_tasks&page=${commonTasksPage}&page_size=${PAGE_SIZE}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter common tasks (assigned_to is empty array) and has 'cness_tasks' in dimension
      const common = (res.data.tasks || []).filter(t => 
        (!t.assigned_to || (Array.isArray(t.assigned_to) && t.assigned_to.length === 0)) &&
        t.dimension && Array.isArray(t.dimension) && t.dimension.includes('cness_tasks')
      );
      
      setCommonTasks(common);
      setCommonTasksTotalPages(res.data.total_pages || 1);
      setCommonTasksTotal(common.length);
    } catch (error) {
      console.error('Failed to fetch common tasks:', error);
    }
  };

  const fetchTeamTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch all team members' tasks for this manager
      const res = await axios.get(`${API_URL}/tasks?dimension=cness_tasks&page=${teamTasksPage}&page_size=${PAGE_SIZE}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter tasks assigned to team members (not the manager, not common tasks)
      // Tasks that have assigned_to with at least one user and has 'cness_tasks' in dimension
      const team = (res.data.tasks || []).filter(t => 
        t.assigned_to && Array.isArray(t.assigned_to) && t.assigned_to.length > 0 &&
        !t.assigned_to.includes(user?.id) && // Exclude tasks assigned to the manager themselves
        t.dimension && Array.isArray(t.dimension) && t.dimension.includes('cness_tasks')
      );
      
      setTeamTasks(team);
      setTeamTasksTotalPages(res.data.total_pages || 1);
      setTeamTasksTotal(team.length);
    } catch (error) {
      console.error('Failed to fetch team tasks:', error);
    }
  };

  const fetchAssignedByMeTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch all tasks where current user is the assigner
      const res = await axios.get(`${API_URL}/tasks?dimension=cness_tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter tasks where assigned_by equals current user
      const assigned = (res.data.tasks || []).filter(t => 
        t.assigned_by === user?.id &&
        t.dimension && Array.isArray(t.dimension) && t.dimension.includes('cness_tasks')
      );
      
      setAssignedByMeTasks(assigned);
    } catch (error) {
      console.error('Failed to fetch assigned by me tasks:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchManagementTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/tasks?dimension=cness_tasks&page=${managementPage}&page_size=${PAGE_SIZE}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter to only include cness_tasks dimension
      const filtered = (res.data.tasks || []).filter(t => 
        t.dimension && Array.isArray(t.dimension) && t.dimension.includes('cness_tasks')
      );
      
      setManagementTasks(filtered);
      setManagementTotalPages(res.data.total_pages || 1);
      setManagementTotal(res.data.total || filtered.length);
    } catch (error) {
      console.error('Failed to fetch all tasks:', error);
    }
  };

  // Reload all tasks when management page changes
  useEffect(() => {
    if (activeTab === 'management') {
      fetchManagementTasks();
    }
  }, [managementPage, activeTab]);

  const fetchCompletedTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/tasks/completed?dimension=cness_tasks&page=${completedPage}&page_size=${PAGE_SIZE}`;
      
      // Add date filters if set
      if (completedDateFrom) {
        url += `&from_date=${completedDateFrom}`;
      }
      if (completedDateTo) {
        url += `&to_date=${completedDateTo}`;
      }
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCompletedTasks(res.data.tasks || []);
      setCompletedTotalPages(res.data.total_pages || 1);
      setCompletedTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch completed tasks:', error);
      // Fallback: filter from all tasks if endpoint doesn't exist
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/tasks?dimension=cness_tasks&status=completed&page=${completedPage}&page_size=100`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        let tasks = (res.data.tasks || []).filter(t => 
          t.status === 'completed' && 
          t.dimension && Array.isArray(t.dimension) && t.dimension.includes('cness_tasks') &&
          (t.assigned_to?.includes(user?.id) || t.assigned_by === user?.id)
        );
        
        // Apply date filters
        if (completedDateFrom) {
          const fromDate = new Date(completedDateFrom);
          tasks = tasks.filter(t => new Date(t.created_at) >= fromDate);
        }
        if (completedDateTo) {
          const toDate = new Date(completedDateTo);
          toDate.setHours(23, 59, 59, 999);
          tasks = tasks.filter(t => new Date(t.created_at) <= toDate);
        }
        
        setCompletedTasks(tasks);
        setCompletedTotalPages(Math.ceil(tasks.length / PAGE_SIZE) || 1);
        setCompletedTotal(tasks.length);
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
      }
    }
  };

  // Reload completed tasks when page or date filters change
  useEffect(() => {
    if (activeTab === 'completed') {
      fetchCompletedTasks();
    }
  }, [completedPage, completedDateFrom, completedDateTo, activeTab]);

  const handleDeleteTask = async (e, taskId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task deleted successfully');
      fetchMyTasks();
      fetchCommonTasks();
      fetchCompletedTasks();
      if (user?.role === 'manager') {
        fetchTeamTasks();
      }
      if (user?.role === 'manager' || user?.role === 'ceo' || user?.role === 'team_lead' || user?.role === 'superadmin') {
        fetchAssignedByMeTasks();
        fetchManagementTasks();
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const handlePickTask = async (e, taskId) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/tasks/${taskId}/pick`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task assigned to you! Check "Assigned to Me" tab.');
      fetchMyTasks();
      fetchCommonTasks();
      if (user?.role === 'manager') {
        fetchTeamTasks();
      }
      if (user?.role === 'manager' || user?.role === 'ceo' || user?.role === 'team_lead' || user?.role === 'superadmin') {
        fetchAssignedByMeTasks();
        fetchManagementTasks();
      }
      // Switch to "Assigned to Me" tab
      setActiveTab('assigned');
    } catch (error) {
      console.error('Failed to pick task:', error);
      toast.error(error.response?.data?.detail || 'Failed to pick task');
    }
  };

  const handleUnpickTask = async (e, taskId) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/tasks/${taskId}/unpick`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task returned to Common Tasks');
      fetchMyTasks();
      fetchCommonTasks();
      if (user?.role === 'manager') {
        fetchTeamTasks();
      }
      if (user?.role === 'manager' || user?.role === 'ceo' || user?.role === 'team_lead' || user?.role === 'superadmin') {
        fetchAssignedByMeTasks();
        fetchManagementTasks();
      }
    } catch (error) {
      console.error('Failed to unpick task:', error);
      toast.error(error.response?.data?.detail || 'Failed to unpick task');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#10B981';
      case 'in_progress': return '#3B82F6';
      case 'pending': return '#F59E0B';
      default: return '#6B7280';
    }
  };

  const getPriorityBadge = (priority) => {
    // Handle numeric priority (1-10) or legacy string priority
    if (typeof priority === 'number') {
      const getPriorityColor = (p) => {
        if (p >= 8) return '#EF4444'; // High (8-10)
        if (p >= 5) return '#F59E0B'; // Medium (5-7)
        return '#10B981'; // Low (1-4)
      };
      const color = getPriorityColor(priority);
      return (
        <span style={{
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: '600',
          background: `${color}15`,
          color: color
        }}>
          P{priority}
        </span>
      );
    }
    // Legacy string priority
    const colors = { high: '#EF4444', medium: '#F59E0B', low: '#10B981' };
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '600',
        background: `${colors[priority] || '#6B7280'}15`,
        color: colors[priority] || '#6B7280',
        textTransform: 'capitalize'
      }}>
        {priority}
      </span>
    );
  };

  const getUserName = (userId) => {
    const u = users.find(u => u.id === userId);
    if (!u) return 'Unknown';
    // Clean name by removing trailing numbers
    return u.full_name.replace(/\s+\d+$/, '').trim();
  };

  const canEditTask = (task) => {
    // User can edit if they created the task (assigned_by) or are superadmin/manager
    return task.assigned_by === user?.id || 
           user?.role === 'superadmin' || 
           user?.role === 'ceo' ||
           user?.role === 'manager';
  };

  const canDeleteTask = (task) => {
    // Same as edit - creator or admin
    return canEditTask(task);
  };

  const renderTaskCard = (task, isCommonTask = false, isTeamTask = false, isAssignedByMe = false) => {
    // Get assignee info for avatars
    const assigneeUsers = (task.assigned_to || []).map(id => {
      const u = users.find(usr => usr.id === id);
      return u ? { id, full_name: u.full_name, avatar_url: u.avatar_url } : { id, full_name: 'Unknown' };
    });

    return (
      <div 
        key={task.id} 
        className="card" 
        style={{ padding: '20px', cursor: 'pointer' }}
        onClick={() => navigate(`/dashboard/tasks/${task.id}`)}
        data-testid={`task-card-${task.id}`}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', flex: 1 }}>{task.title}</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {getPriorityBadge(task.priority)}
            <span style={{
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '600',
              background: `${getStatusColor(task.status)}15`,
              color: getStatusColor(task.status),
              textTransform: 'capitalize'
            }}>
              {task.status?.replace('_', ' ')}
            </span>
          </div>
        </div>
        <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
          {getPlainTextPreview(task.description, 150)}
        </p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {/* Show assignee avatars */}
            {(isTeamTask || isAssignedByMe || !isCommonTask) && assigneeUsers.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AvatarStack users={assigneeUsers} maxVisible={3} size={26} />
              </div>
            )}
            {task.deadline && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Clock size={12} />
                Due: {formatDateSafe(task.deadline)}
              </span>
            )}
            {isCommonTask && task.picked_by?.length > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#3B82F6' }}>
                <Users size={12} />
                {task.picked_by.length} picked
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Pick/Unpick for common tasks */}
            {isCommonTask && (
              task.picked_by?.includes(user?.id) ? (
                <button
                  onClick={(e) => handleUnpickTask(e, task.id)}
                  style={{
                    padding: '4px 8px',
                    background: '#FEE2E2',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '12px',
                    color: '#EF4444'
                  }}
                  title="Unpick task"
                >
                  <UserCheck size={14} /> Unpick
                </button>
              ) : (
                <button
                  onClick={(e) => handlePickTask(e, task.id)}
                style={{
                  padding: '4px 8px',
                  background: '#DBEAFE',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  color: '#3B82F6'
                }}
                title="Pick task"
              >
                <Plus size={14} /> Pick
              </button>
            )
          )}
          {/* Edit button */}
          {canEditTask(task) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/dashboard/tasks/edit/${task.id}`);
              }}
              style={{
                padding: '6px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#3B82F6'
              }}
              title="Edit task"
            >
              <Edit2 size={16} />
            </button>
          )}
          {/* Delete button */}
          {canDeleteTask(task) && (
            <button
              onClick={(e) => handleDeleteTask(e, task.id)}
              style={{
                padding: '8px 10px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#EF4444',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              title="Delete task"
              data-testid={`delete-task-${task.id}`}
            >
              <Trash2 size={16} />
            </button>
          )}
          <ChevronRight size={18} style={{ color: 'var(--text-tertiary)' }} />
        </div>
      </div>
    </div>
    );
  };

  const renderPagination = (currentPage, totalPages, setPage) => {
    if (totalPages <= 1) return null;
    
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        gap: '16px', 
        marginTop: '24px' 
      }}>
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          style={{
            padding: '8px 16px',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            backgroundColor: currentPage === 1 ? 'var(--bg-secondary)' : 'white',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            opacity: currentPage === 1 ? 0.5 : 1
          }}
        >
          <ChevronLeft size={16} /> Previous
        </button>
        <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          style={{
            padding: '8px 16px',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            backgroundColor: currentPage === totalPages ? 'var(--bg-secondary)' : 'white',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            opacity: currentPage === totalPages ? 0.5 : 1
          }}
        >
          Next <ChevronRight size={16} />
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="content-area" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const allTasks = user?.role === 'manager' 
    ? [...myTasks, ...commonTasks.filter(ct => !ct.picked_by?.includes(user?.id)), ...teamTasks]
    : [...myTasks, ...commonTasks.filter(ct => !ct.picked_by?.includes(user?.id))];

  return (
    <div style={{ display: 'flex', gap: '24px', padding: '24px', maxWidth: '100%' }}>
      {/* Main Content Area */}
      <div style={{ flex: 1, minWidth: 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <ClipboardList size={28} style={{ color: '#241665' }} />
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>Tasks</h1>
          </div>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
            Your daily tasks and responsibilities
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button 
            onClick={() => navigate('/dashboard/tasks')} 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              padding: '12px 20px',
              backgroundColor: '#EEF2FF',
              border: '1px solid #A5B4FC',
              borderRadius: '8px',
              color: '#4338CA',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <ClipboardList size={18} /> View All Tasks
          </button>
          <button 
            onClick={() => navigate('/dashboard/sops')} 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              padding: '12px 20px',
              backgroundColor: '#F0FDF4',
              border: '1px solid #86EFAC',
              borderRadius: '8px',
              color: '#15803D',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            <FileText size={18} /> View My SOPs {mySOPs.length > 0 && `(${mySOPs.length})`}
          </button>
          <button 
            onClick={() => navigate('/dashboard/tasks/new')} 
            className="gradient-btn" 
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
          >
            <Plus size={18} /> New Task
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <ClipboardList size={24} style={{ color: '#241665', marginBottom: '8px' }} />
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#241665' }}>{allTasks.length}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Total Tasks</div>
        </div>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <Clock size={24} style={{ color: '#F59E0B', marginBottom: '8px' }} />
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#F59E0B' }}>{allTasks.filter(t => t.status === 'pending').length}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Pending</div>
        </div>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <AlertCircle size={24} style={{ color: '#3B82F6', marginBottom: '8px' }} />
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#3B82F6' }}>{allTasks.filter(t => t.status === 'in_progress').length}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>In Progress</div>
        </div>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <CheckCircle size={24} style={{ color: '#10B981', marginBottom: '8px' }} />
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#10B981' }}>{allTasks.filter(t => t.status === 'completed').length}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Completed</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid var(--border-color)', flexWrap: 'wrap' }}>
        <button
          onClick={() => setActiveTab('assigned')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            color: activeTab === 'assigned' ? '#241665' : 'var(--text-tertiary)',
            borderBottom: activeTab === 'assigned' ? '3px solid #241665' : '3px solid transparent',
            marginBottom: '-1px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          data-testid="tab-assigned-to-me"
        >
          <UserCheck size={18} />
          Assigned to Me ({myTasks.length})
        </button>
        
        {(user?.role === 'manager' || user?.role === 'ceo' || user?.role === 'team_lead' || user?.role === 'superadmin') && (
          <button
            onClick={() => setActiveTab('assigned_by_me')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: activeTab === 'assigned_by_me' ? '#8B5CF6' : 'var(--text-tertiary)',
              borderBottom: activeTab === 'assigned_by_me' ? '3px solid #8B5CF6' : '3px solid transparent',
              marginBottom: '-1px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            data-testid="tab-assigned-by-me"
          >
            <Users size={18} />
            Assigned by Me ({assignedByMeTasks.length})
          </button>
        )}
        
        {user?.role === 'manager' && (
          <button
            onClick={() => setActiveTab('team')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: activeTab === 'team' ? '#241665' : 'var(--text-tertiary)',
              borderBottom: activeTab === 'team' ? '3px solid #241665' : '3px solid transparent',
              marginBottom: '-1px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Users size={18} />
            Team Members Tasks ({teamTasks.length})
          </button>
        )}
        
        <button
          onClick={() => setActiveTab('common')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            color: activeTab === 'common' ? '#241665' : 'var(--text-tertiary)',
            borderBottom: activeTab === 'common' ? '3px solid #241665' : '3px solid transparent',
            marginBottom: '-1px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Users size={18} />
          Common Tasks ({commonTasks.length})
        </button>
        
        {/* Task Management Tab - visible to managers, CEO, team leads, superadmin */}
        {(user?.role === 'manager' || user?.role === 'ceo' || user?.role === 'team_lead' || user?.role === 'superadmin') && (
          <button
            onClick={() => setActiveTab('management')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: activeTab === 'management' ? '#059669' : 'var(--text-tertiary)',
              borderBottom: activeTab === 'management' ? '3px solid #059669' : '3px solid transparent',
              marginBottom: '-1px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            data-testid="tab-task-management"
          >
            <LayoutGrid size={18} />
            Task Management
          </button>
        )}
        
        {/* Completed Tasks Tab */}
        <button
          onClick={() => setActiveTab('completed')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            color: activeTab === 'completed' ? '#10B981' : 'var(--text-tertiary)',
            borderBottom: activeTab === 'completed' ? '3px solid #10B981' : '3px solid transparent',
            marginBottom: '-1px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          data-testid="tab-completed-tasks"
        >
          <CheckCircle size={18} />
          Completed ({completedTotal})
        </button>
        
        {/* Work Planning Tab */}
        <button
          onClick={() => setActiveTab('planning')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            color: activeTab === 'planning' ? '#F59E0B' : 'var(--text-tertiary)',
            borderBottom: activeTab === 'planning' ? '3px solid #F59E0B' : '3px solid transparent',
            marginBottom: '-1px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          data-testid="tab-work-planning"
        >
          <Target size={18} />
          Work Planning
        </button>
      </div>

      {/* Work Planning Tab Content */}
      {activeTab === 'planning' ? (
        <WorkPlanning user={user} />
      ) : activeTab === 'assigned' ? (
        <>
          {myTasks.length === 0 ? (
            <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
              <UserCheck size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No tasks assigned to you</h3>
              <p style={{ margin: '0 0 20px 0', color: 'var(--text-tertiary)' }}>Check the Common Tasks tab for tasks you can pick up</p>
              <button onClick={() => setActiveTab('common')} className="gradient-btn">View Common Tasks</button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {myTasks.map(task => renderTaskCard(task, false))}
              </div>
              {renderPagination(myTasksPage, myTasksTotalPages, setMyTasksPage)}
            </>
          )}
        </>
      ) : activeTab === 'assigned_by_me' ? (
        <>
          {assignedByMeTasks.length === 0 ? (
            <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
              <Users size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No tasks assigned by you</h3>
              <p style={{ margin: '0 0 20px 0', color: 'var(--text-tertiary)' }}>Tasks you create and assign to others will appear here</p>
              <button onClick={() => navigate('/dashboard/tasks/new')} className="gradient-btn">Create New Task</button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {assignedByMeTasks.map(task => renderTaskCard(task, false, false, true))}
              </div>
            </>
          )}
        </>
      ) : activeTab === 'team' ? (
        <>
          {teamTasks.length === 0 ? (
            <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
              <Users size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No team tasks found</h3>
              <p style={{ margin: '0 0 20px 0', color: 'var(--text-tertiary)' }}>Tasks assigned to your team members will appear here</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {teamTasks.map(task => renderTaskCard(task, false, true))}
              </div>
              {renderPagination(teamTasksPage, teamTasksTotalPages, setTeamTasksPage)}
            </>
          )}
        </>
      ) : activeTab === 'common' ? (
        <>
          {commonTasks.length === 0 ? (
            <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
              <Users size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No common tasks available</h3>
              <p style={{ margin: '0 0 20px 0', color: 'var(--text-tertiary)' }}>Create a task without assigning it to make it a common task</p>
              <button onClick={() => navigate('/dashboard/tasks/new')} className="gradient-btn">Create Task</button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {commonTasks.map(task => renderTaskCard(task, true))}
              </div>
              {renderPagination(commonTasksPage, commonTasksTotalPages, setCommonTasksPage)}
            </>
          )}
        </>
      ) : activeTab === 'management' ? (
        /* Task Management View - Grid/List of all tasks with full details */
        <>
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ 
              padding: '14px 20px',
              background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.1), rgba(16, 185, 129, 0.1))',
              borderRadius: '10px',
              border: '1px solid #059669',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <LayoutGrid size={24} style={{ color: '#059669' }} />
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#059669' }}>
                  Task Management ({managementTotal} tasks)
                </h3>
                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                  Full control over all tasks - view, edit, delete
                </p>
              </div>
            </div>
          </div>
          
          {managementTasks.length === 0 ? (
            <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
              <LayoutGrid size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No tasks found</h3>
              <p style={{ margin: '0 0 20px 0', color: 'var(--text-tertiary)' }}>Create your first task to get started</p>
              <button onClick={() => navigate('/dashboard/tasks/new')} className="gradient-btn">Create Task</button>
            </div>
          ) : (
            <>
              {/* Task Management Grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                gap: '16px'
              }}>
                {managementTasks.map(task => {
                  const isCreator = task.assigned_by === user?.id;
                  const assignedToNames = task.assigned_to && task.assigned_to.length > 0 
                    ? task.assigned_to.map(id => getUserName(id)).join(', ')
                    : 'Unassigned';
                  
                  return (
                    <div
                      key={task.id}
                      className="card"
                      style={{ 
                        padding: '20px',
                        borderLeft: `4px solid ${getStatusColor(task.status)}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => navigate(`/dashboard/tasks/${task.id}`)}
                      data-testid={`management-task-${task.id}`}
                    >
                      {/* Header with title and actions */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <h4 style={{ 
                          margin: 0, 
                          fontSize: '16px', 
                          fontWeight: '600', 
                          color: 'var(--text-primary)',
                          flex: 1,
                          paddingRight: '12px'
                        }}>
                          {task.title}
                        </h4>
                        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                          {getPriorityBadge(task.priority)}
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            background: `${getStatusColor(task.status)}15`,
                            color: getStatusColor(task.status),
                            textTransform: 'capitalize'
                          }}>
                            {task.status?.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                      
                      {/* Description */}
                      <p style={{ 
                        margin: '0 0 12px 0', 
                        fontSize: '13px', 
                        color: 'var(--text-secondary)',
                        lineHeight: '1.5',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {getPlainTextPreview(task.description, 120)}
                      </p>
                      
                      {/* Meta Info Grid */}
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr',
                        gap: '8px',
                        marginBottom: '12px',
                        padding: '12px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}>
                        <div>
                          <span style={{ color: 'var(--text-tertiary)' }}>Assigned to:</span>
                          <div style={{ color: 'var(--text-primary)', fontWeight: '500', marginTop: '2px' }}>
                            {assignedToNames}
                          </div>
                        </div>
                        <div>
                          <span style={{ color: 'var(--text-tertiary)' }}>Created by:</span>
                          <div style={{ color: 'var(--text-primary)', fontWeight: '500', marginTop: '2px' }}>
                            {getUserName(task.assigned_by)}
                          </div>
                        </div>
                        {task.deadline && (
                          <div>
                            <span style={{ color: 'var(--text-tertiary)' }}>Deadline:</span>
                            <div style={{ 
                              color: isDeadlineOverdue(task.deadline) && task.status !== 'completed' ? '#EF4444' : 'var(--text-primary)', 
                              fontWeight: '500', 
                              marginTop: '2px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <Calendar size={12} />
                              {formatDateSafe(task.deadline)}
                            </div>
                          </div>
                        )}
                        <div>
                          <span style={{ color: 'var(--text-tertiary)' }}>Created:</span>
                          <div style={{ color: 'var(--text-primary)', fontWeight: '500', marginTop: '2px' }}>
                            {new Date(task.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/tasks/${task.id}`);
                          }}
                          style={{
                            padding: '6px 12px',
                            background: '#EEF2FF',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '12px',
                            color: '#4338CA',
                            fontWeight: '500'
                          }}
                        >
                          <Eye size={14} /> View
                        </button>
                        {canEditTask(task) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/tasks/edit/${task.id}`);
                            }}
                            style={{
                              padding: '6px 12px',
                              background: '#DBEAFE',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '12px',
                              color: '#1D4ED8',
                              fontWeight: '500'
                            }}
                          >
                            <Edit2 size={14} /> Edit
                          </button>
                        )}
                        {canDeleteTask(task) && (
                          <button
                            onClick={(e) => handleDeleteTask(e, task.id)}
                            style={{
                              padding: '6px 12px',
                              background: '#FEE2E2',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '12px',
                              color: '#DC2626',
                              fontWeight: '500'
                            }}
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        )}
                      </div>
                      
                      {/* Creator Badge */}
                      {isCreator && (
                        <div style={{
                          marginTop: '8px',
                          padding: '4px 8px',
                          background: '#D1FAE5',
                          borderRadius: '4px',
                          fontSize: '11px',
                          color: '#059669',
                          fontWeight: '600',
                          display: 'inline-block'
                        }}>
                          You created this task
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {renderPagination(managementPage, managementTotalPages, setManagementPage)}
            </>
          )}
        </>
      ) : activeTab === 'completed' ? (
        /* Completed Tasks View - Date-wise with filters */
        <>
          {/* Date Filter Section */}
          <div style={{ 
            marginBottom: '20px', 
            padding: '16px 20px',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(52, 211, 153, 0.08))',
            borderRadius: '12px',
            border: '1px solid rgba(16, 185, 129, 0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <CalendarDays size={20} style={{ color: '#10B981' }} />
                <span style={{ fontWeight: '600', color: '#059669' }}>Filter by Date</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>From:</label>
                  <input
                    type="date"
                    value={completedDateFrom}
                    onChange={(e) => {
                      setCompletedDateFrom(e.target.value);
                      setCompletedPage(1);
                    }}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>To:</label>
                  <input
                    type="date"
                    value={completedDateTo}
                    onChange={(e) => {
                      setCompletedDateTo(e.target.value);
                      setCompletedPage(1);
                    }}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>
                {(completedDateFrom || completedDateTo) && (
                  <button
                    onClick={() => {
                      setCompletedDateFrom('');
                      setCompletedDateTo('');
                      setCompletedPage(1);
                    }}
                    style={{
                      padding: '8px 12px',
                      background: '#FEE2E2',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: '#DC2626',
                      fontWeight: '500'
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Completed Tasks Info */}
          <div style={{ 
            marginBottom: '16px',
            padding: '12px 16px',
            background: 'var(--bg-secondary)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <CheckCircle size={18} style={{ color: '#10B981' }} />
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              Showing tasks completed by you and tasks you assigned that were completed
              {completedDateFrom && completedDateTo && ` from ${new Date(completedDateFrom).toLocaleDateString()} to ${new Date(completedDateTo).toLocaleDateString()}`}
            </span>
          </div>
          
          {completedTasks.length === 0 ? (
            <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
              <CheckCircle size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No completed tasks found</h3>
              <p style={{ margin: '0 0 20px 0', color: 'var(--text-tertiary)' }}>
                {(completedDateFrom || completedDateTo) 
                  ? 'Try adjusting your date filters' 
                  : 'Tasks you complete or tasks assigned by you that are completed will appear here'}
              </p>
            </div>
          ) : (
            <>
              {/* Group tasks by date */}
              {(() => {
                // Group tasks by completion/update date
                const groupedByDate = completedTasks.reduce((acc, task) => {
                  const dateKey = new Date(task.updated_at || task.created_at).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  });
                  if (!acc[dateKey]) acc[dateKey] = [];
                  acc[dateKey].push(task);
                  return acc;
                }, {});

                return Object.entries(groupedByDate).map(([date, tasks]) => (
                  <div key={date} style={{ marginBottom: '24px' }}>
                    {/* Date Header */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px',
                      paddingBottom: '8px',
                      borderBottom: '2px solid #10B981'
                    }}>
                      <Calendar size={18} style={{ color: '#10B981' }} />
                      <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#059669' }}>
                        {date}
                      </h3>
                      <span style={{
                        padding: '2px 8px',
                        background: 'rgba(16, 185, 129, 0.1)',
                        borderRadius: '12px',
                        fontSize: '12px',
                        color: '#10B981',
                        fontWeight: '600'
                      }}>
                        {tasks.length} task{tasks.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    
                    {/* Tasks for this date */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {tasks.map(task => {
                        const isCompletedByMe = task.assigned_to?.includes(user?.id);
                        const isAssignedByMe = task.assigned_by === user?.id;
                        const assignedToNames = task.assigned_to && task.assigned_to.length > 0 
                          ? task.assigned_to.map(id => getUserName(id)).join(', ')
                          : 'Unassigned';
                        
                        return (
                          <div
                            key={task.id}
                            className="card"
                            style={{ 
                              padding: '16px',
                              borderLeft: '4px solid #10B981',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onClick={() => navigate(`/dashboard/tasks/${task.id}`)}
                            data-testid={`completed-task-${task.id}`}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                  <CheckCircle size={16} style={{ color: '#10B981' }} />
                                  <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>
                                    {task.title}
                                  </h4>
                                </div>
                                <p style={{ 
                                  margin: '0 0 8px 24px', 
                                  fontSize: '13px', 
                                  color: 'var(--text-secondary)',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}>
                                  {getPlainTextPreview(task.description, 100)}
                                </p>
                              </div>
                              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                {isCompletedByMe && (
                                  <span style={{
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    background: '#D1FAE5',
                                    color: '#059669'
                                  }}>
                                    Completed by me
                                  </span>
                                )}
                                {isAssignedByMe && !isCompletedByMe && (
                                  <span style={{
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    background: '#DBEAFE',
                                    color: '#1D4ED8'
                                  }}>
                                    Assigned by me
                                  </span>
                                )}
                                {getPriorityBadge(task.priority)}
                              </div>
                            </div>
                            
                            {/* Meta info */}
                            <div style={{ 
                              display: 'flex', 
                              gap: '16px', 
                              marginLeft: '24px',
                              fontSize: '12px',
                              color: 'var(--text-tertiary)'
                            }}>
                              {isAssignedByMe && (
                                <span>Completed by: <strong style={{ color: 'var(--text-secondary)' }}>{assignedToNames}</strong></span>
                              )}
                              {isCompletedByMe && task.assigned_by && (
                                <span>Assigned by: <strong style={{ color: 'var(--text-secondary)' }}>{getUserName(task.assigned_by)}</strong></span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
              {renderPagination(completedPage, completedTotalPages, setCompletedPage)}
            </>
          )}
        </>
      ) : null}
    </div>
    
    {/* Right Sidebar - Assigned Tasks (for Managers, CEO, Team Leads) */}
    {(user?.role === 'manager' || user?.role === 'ceo' || user?.role === 'team_lead') && (
      <div style={{ width: '320px', flexShrink: 0 }}>
        <div style={{
          background: 'var(--bg-primary)',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          padding: '20px',
          position: 'sticky',
          top: '24px',
          maxHeight: 'calc(100vh - 100px)',
          overflowY: 'auto'
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '16px', 
            fontWeight: '600',
            color: 'var(--text-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <UserCheck size={18} style={{ color: '#7077FE' }} />
            Tasks Assigned by Me
          </h3>
          
          {assignedByMeTasks.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              color: 'var(--text-tertiary)',
              fontSize: '14px'
            }}>
              <ClipboardList size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
              <p style={{ margin: 0 }}>No tasks assigned yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {assignedByMeTasks.map(task => (
                <div
                  key={task.id}
                  onClick={() => navigate(`/dashboard/tasks/${task.id}`)}
                  style={{
                    padding: '12px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#7077FE';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(112, 119, 254, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ marginBottom: '8px' }}>
                    <h4 style={{ 
                      margin: '0 0 4px 0', 
                      fontSize: '14px', 
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {task.title}
                    </h4>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '12px', 
                      color: 'var(--text-secondary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {getPlainTextPreview(task.description, 50)}
                    </p>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: task.priority >= 8 ? '#FEE2E2' : task.priority >= 5 ? '#FEF3C7' : '#D1FAE5',
                      color: task.priority >= 8 ? '#DC2626' : task.priority >= 5 ? '#D97706' : '#059669'
                    }}>
                      P{task.priority}
                    </span>
                    <span className={`status-badge ${task.status}`} style={{
                      fontSize: '10px',
                      padding: '2px 6px'
                    }}>
                      {task.status}
                    </span>
                    {task.assigned_to && task.assigned_to.length > 0 && (
                      <span style={{
                        fontSize: '11px',
                        color: 'var(--text-tertiary)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Users size={10} />
                        {task.assigned_to.length}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div style={{ 
            marginTop: '16px', 
            paddingTop: '16px',
            borderTop: '1px solid var(--border-color)',
            fontSize: '12px',
            color: 'var(--text-tertiary)',
            textAlign: 'center'
          }}>
            Total: {assignedByMeTasks.length} task{assignedByMeTasks.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    )}
  </div>
  );
};

export default CNESSTasks;
